import { clsx } from "clsx"
import { forwardRef, type MouseEvent, type ReactNode } from "react"
import { type Keys, useHotkeys } from "react-hotkeys-hook"

import type { SX } from "../../theme"
import { useButtonGroup } from "../ButtonGroup"
import { HiddenLabel } from "../HiddenLabel"
import { Icon, type IconName } from "../Icon"
import { ProgressCircle } from "../ProgressCircle"
import { Text } from "../Text"
import styles from "./Button.module.css"

export interface ButtonProps {
  children?: ReactNode
  /** Note that white is only supported for the invisible button */
  color?:
    | "grey"
    | "purple"
    | "dark"
    | "tomato"
    | "amber"
    | "green"
    | "white"
    | "indigo"
  size?: "small" | "medium" | "large"
  disabled?: boolean
  loading?: boolean

  textColor?: "placeholder" | "link"

  /**
   * Whether to make the text (children) content of the Button take all the available space.
   * This can be useful when the Button's width is set from the outside instead of being "auto".
   * If you set a large width from the outside but leave this prop to false/undefined,
   * the button's children will be centered, which looks strange.
   * Defaults to false.
   */
  flexContent?: boolean

  /**
   * Buttons have a bold text by default. You can override this by passing "normal".
   */
  textWeight?: "normal" | "bold"
  sx?: SX

  startIcon?: IconName
  endIcon?: IconName
  renderStartIcon?: (icon: ReactNode) => ReactNode
  renderEndIcon?: (icon: ReactNode) => ReactNode

  hotkeys?: Keys
  onClick?: (event: MouseEvent | KeyboardEvent) => void
  onMouseEnter?: (event: MouseEvent) => void
  onMouseLeave?: (event: MouseEvent) => void
  onMouseDown?: (event: MouseEvent) => void
  onMouseUp?: (event: MouseEvent) => void

  /** Invisible variant in the design system. Used for icon buttons with no background */
  invisible?: boolean
  /** Removes padding around the icon. Will only apply to icon only buttons. */
  hasPadding?: boolean
  /**  Underlines the text content of the button. Only applies to invisible buttons */
  underline?: boolean

  hiddenLabel?: string
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    const buttonGroup = useButtonGroup()

    const {
      children,
      disabled = buttonGroup?.disabled,
      startIcon,
      endIcon,
      renderStartIcon = (icon) => icon,
      renderEndIcon = (icon) => icon,
      loading = false,
      invisible = false,
      underline,
      size = buttonGroup?.size ?? (invisible ? "large" : "medium"),
      color = buttonGroup?.color ?? "purple",
      textColor,
      textWeight = "bold",
      flexContent = false,
      sx,
      hiddenLabel,
      hasPadding = true,
      hotkeys,
      onClick,
      onMouseDown,
      onMouseUp,
      onMouseEnter,
      onMouseLeave,
      ...implicitProps
    } = props

    const iconColor = getIconColor(color, invisible)
    const iconSize = size === "small" ? "small" : "medium"

    useHotkeys(
      hotkeys ?? [],
      (e) => onClick?.(e),
      {
        enabled:
          !disabled &&
          !loading &&
          // "react-hotkeys-hook" sometimes triggers the callback when no hotkeys are specified.
          // The following check is needed to prevent that. (See: PBD-1604, PBD-1629)
          Boolean(hotkeys?.length),
        preventDefault: true,
        enableOnFormTags: true,
        enableOnContentEditable: true
      },
      [onClick]
    )

    return (
      <button
        {...implicitProps}
        className={clsx(
          styles.button,
          styles[`size-${size}`],
          styles[`color-${color}`],
          styles[`gap-${flexContent ? 8 : 4}`],
          invisible && styles.invisible,
          !hasPadding && styles.noPadding,
          loading && styles.loading,
          !children && styles.iconOnly,
          underline && styles.underline,
          // disabled is not only an attribute but also a className here
          // so that the loading state doesn't look disabled, even though it physically is.
          disabled && styles.disabled
        )}
        style={sx}
        disabled={disabled || loading}
        ref={ref}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {renderStartIcon(
          startIcon && (
            <Icon name={startIcon} color={iconColor} size={iconSize} />
          )
        )}

        {children && (
          <Text
            color={getTextColor(textColor)}
            component="span"
            variant={getTextVariant(textWeight, size)}
            noWrap
            sx={flexContent ? { flexGrow: 1 } : undefined}
          >
            {children}
          </Text>
        )}

        {renderEndIcon(
          endIcon && <Icon name={endIcon} color={iconColor} size={iconSize} />
        )}

        {loading && (
          <div className={styles.loadingIndicator}>
            <ProgressCircle />
          </div>
        )}
        {hiddenLabel && <HiddenLabel>{hiddenLabel}</HiddenLabel>}
      </button>
    )
  }
)

function getTextColor(textColor?: "placeholder" | "link") {
  if (textColor === "placeholder") return "grey11"
  if (textColor === "link") return "indigo11"
  return "inherit"
}

function getTextVariant(
  textWeight: ButtonProps["textWeight"],
  size: ButtonProps["size"]
) {
  if (textWeight === "normal") return size === "small" ? "small" : "normal"
  return size === "large" ? "emphasized" : "smallBold"
}

function getIconColor(color: ButtonProps["color"], invisible: boolean) {
  if (!invisible || !color) return color === "grey" ? "grey11" : "white"

  return undefined
}
